import cx from 'classnames';
import { useEffect, useRef, useState, type FunctionComponent } from 'react';
import { useIntl } from 'react-intl';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { getProposedPanelWidth } from '@trello/split-screen/';
import type { SplitScreenTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import styles from './SplitScreenResizeDivider.less';

interface SplitScreenResizeDividerProps {
  onDragStartCallback: () => void;
  doubleClickCallback: () => void;
  draggingPanelCallback: (newWidth: number) => void;
  dropPanelCallback: (newWidth: number) => void;
  panelMinWidth: number;
  panelMaxWidth: number;
  panelPreviousWidth: number;
  isHidden: boolean;
}

export const SplitScreenResizeDivider: FunctionComponent<
  SplitScreenResizeDividerProps
> = ({
  onDragStartCallback,
  draggingPanelCallback,
  doubleClickCallback,
  dropPanelCallback,
  panelMinWidth,
  panelMaxWidth,
  panelPreviousWidth,
  isHidden,
}) => {
  const intl = useIntl();
  const dividerRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const divider = dividerRef.current;

    if (!divider) {
      return;
    }

    return draggable({
      element: divider,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
        preventUnhandled.start();
      },
      onDragStart() {
        onDragStartCallback();
        setIsDragging(true);
      },
      onDrag({ location }) {
        const newWidth = getProposedPanelWidth({
          location,
          maxWidth: panelMaxWidth,
          minWidth: panelMinWidth,
          previousWidth: panelPreviousWidth,
        });
        draggingPanelCallback(newWidth);
      },
      onDrop({ location }) {
        preventUnhandled.stop();

        const newWidth = getProposedPanelWidth({
          location,
          maxWidth: panelMaxWidth,
          minWidth: panelMinWidth,
          previousWidth: panelPreviousWidth,
        });
        dropPanelCallback(newWidth);
        setIsDragging(false);
      },
    });
  }, [
    draggingPanelCallback,
    dropPanelCallback,
    onDragStartCallback,
    panelMaxWidth,
    panelMinWidth,
    panelPreviousWidth,
  ]);

  return (
    <div className={styles.dragAndDropDividerContainer}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions*/}
      <div
        className={cx(styles.dragAndDropDivider, {
          [styles.dragging]: isDragging,
          [styles.hidden]: isHidden,
        })}
        aria-label={intl.formatMessage({
          id: 'templates.split-screen-resize.drag-to-resize',
          defaultMessage: 'Drag to resize',
          description: 'Aria label for the resize divider',
        })}
        role="separator"
        ref={dividerRef}
        data-testid={getTestId<SplitScreenTestIds>('resize-divider')}
        onDoubleClick={doubleClickCallback}
      />
    </div>
  );
};
